"use client"

import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Share2, Download, Copy, X, Check } from "lucide-react"
import { ShareCard } from "./share-card"

interface ShareButtonProps {
  type: "standard" | "daily" | "timed" | "versus"
  score: number
  gameType: string
  extra?: {
    difficulty?: string
    date?: string
    rank?: string
  }
}

export function ShareButton({ type, score, gameType, extra }: ShareButtonProps) {
  const [showModal, setShowModal] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null
    setGenerating(true)
    try {
      const html2canvas = (await import("html2canvas-pro")).default
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
      })
      const url = canvas.toDataURL("image/png")
      setImageUrl(url)
      setGenerating(false)
      return { canvas, url }
    } catch {
      setGenerating(false)
      return null
    }
  }, [])

  const handleOpen = async () => {
    setShowModal(true)
    // Wait for next frame so the card is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        generateImage()
      })
    })
  }

  const handleDownload = async () => {
    const result = imageUrl ? { url: imageUrl } : await generateImage()
    if (!result?.url) return

    const link = document.createElement("a")
    link.download = `higherlower-${type}-score-${score}.png`
    link.href = result.url
    link.click()
  }

  const handleShare = async () => {
    const result = imageUrl ? { url: imageUrl } : await generateImage()
    if (!result?.url) return

    try {
      const blob = await (await fetch(result.url)).blob()
      const file = new File([blob], `higherlower-${type}-score-${score}.png`, { type: "image/png" })

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          text: `I scored ${score} on Higher Lower Game!`,
          files: [file],
        })
      } else {
        // Fallback: copy text
        await navigator.clipboard.writeText(
          `I scored ${score} on Higher Lower Game! Play at https://playhigherlower.com`
        )
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch {
      // User cancelled or error
    }
  }

  const handleCopyText = async () => {
    const difficultyStr = extra?.difficulty ? ` (${extra.difficulty})` : ""
    const modeNames = { standard: "Classic", daily: "Daily Challenge", timed: "Timed Mode", versus: "Versus Mode" }
    const text = `I scored ${score} on ${modeNames[type]}${difficultyStr} - ${gameType}! Play at https://playhigherlower.com`
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        className="h-14 text-lg rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
      >
        <Share2 className="h-5 w-5" />
        Share
      </Button>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-score-title"
        >
          <div className="bg-[#fbf7ef] rounded-[24px] border border-[#171717]/15 max-w-lg w-full overflow-hidden shadow-[0_28px_70px_rgb(0_0_0/0.24)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#d8d0c4]">
              <h3 id="share-score-title" className="text-lg font-bold text-[#171717]">Share Your Score</h3>
              <button
                onClick={() => { setShowModal(false); setImageUrl(null) }}
                className="p-2 rounded-lg text-[#655d54] hover:bg-[#ff6f61]/15 hover:text-[#171717]"
                aria-label="Close share dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Card preview */}
            <div className="p-4">
              {/* Hidden card for rendering */}
              <div style={{ position: "absolute", left: -9999, top: -9999 }}>
                <ShareCard
                  ref={cardRef}
                  type={type}
                  score={score}
                  gameType={gameType}
                  extra={extra}
                />
              </div>

              {/* Image preview */}
              <div className="rounded-xl overflow-hidden border border-[#d8d0c4] bg-[#eee6db] aspect-[3/4] flex items-center justify-center">
                {imageUrl ? (
                  <img src={imageUrl} alt="Share card preview" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-[#655d54] text-sm">
                    {generating ? "Generating..." : "Preparing preview..."}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 pt-0 flex gap-2">
              <Button
                onClick={handleShare}
                disabled={generating}
                className="coral-cta flex-1 h-12 rounded-xl gap-2 border bg-[#ff6f61] text-[#171717]"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
              <Button
                onClick={handleDownload}
                disabled={generating}
                className="h-12 rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                onClick={handleCopyText}
                className="h-12 rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
